import { PlaidConnectionErrors } from "../../constants";
import { assertRelationLoaded } from "../../helpers/orm";
import {
    ConnectionProviderType,
    FinancialConnectionBase,
} from "../../types/financialConnection";
import { IntegrationAccountBase } from "../../types/integrationAccount";
import { PlaidConnectionWithAccounts } from "../../types/plaidConnection";
import { IntegrationAccountDto } from "../financialAccount/integration-account.dto";
import { UserDto } from "../user/user.dto";

/**
 * Input type for PlaidConnectionDto constructor.
 * Combines FinancialConnectionBase with Plaid-specific fields.
 */
export type PlaidConnectionDtoInput = FinancialConnectionBase & {
    bankName: string;
    bankLogoUrl: string | null;
    institutionId: string | null;
    historicalDataProvidedManually: boolean;
    error: PlaidConnectionErrors | null;
    statementsApiSupported: boolean | null;
    statementsApiEnabled: boolean | null;
};

export class PlaidConnectionDto {
    static deserialize(dto: PlaidConnectionDto): PlaidConnectionWithAccounts {
        return {
            ...dto,
            createdAt: new Date(dto.createdAt),
            user: dto.user ? UserDto.deserialize(dto.user) : null,
            accounts: dto.accounts.map(IntegrationAccountDto.deserialize),
        };
    }

    id: number;
    bankName: string;
    error: PlaidConnectionErrors | null;
    accounts: IntegrationAccountDto[];
    bankLogoUrl: string | null;
    institutionId: string | null;
    historicalDataProvidedManually: boolean;
    statementsApiSupported: boolean | null;
    statementsApiEnabled: boolean | null;
    createdAt: string;
    user: UserDto | null;
    userId: number | null;
    connectionProvider: ConnectionProviderType;
    connectionExternalId: string;

    constructor(plaidConnection: PlaidConnectionDtoInput) {
        assertRelationLoaded(plaidConnection, "user");

        this.id = plaidConnection.id;
        this.bankName = plaidConnection.bankName;
        this.error = plaidConnection.error;
        this.accounts = (plaidConnection.accounts ?? []).map(
            (account: IntegrationAccountBase) =>
                new IntegrationAccountDto(account),
        );
        this.bankLogoUrl = plaidConnection.bankLogoUrl;
        this.institutionId = plaidConnection.institutionId;
        this.historicalDataProvidedManually =
            plaidConnection.historicalDataProvidedManually;
        this.statementsApiSupported =
            plaidConnection.statementsApiSupported ?? null;
        this.statementsApiEnabled =
            plaidConnection.statementsApiEnabled ?? null;
        this.createdAt = plaidConnection.createdAt.toISOString();
        this.user = plaidConnection.user
            ? new UserDto(plaidConnection.user)
            : null;
        this.userId = plaidConnection.userId;
        this.connectionProvider = plaidConnection.connectionProvider;
        this.connectionExternalId = plaidConnection.connectionExternalId;
    }
}
